import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { 
  FileText, 
  Download, 
  Calendar, 
  Clock, 
  Users,
  DollarSign,
  RefreshCw,
  FileSpreadsheet,
  Printer,
  TrendingUp,
  AlertTriangle,
  ClipboardList,
  Timer,
  Briefcase,
  CreditCard,
  CheckCircle,
  Banknote
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { formatTimeFromISO } from '@/utils/timezoneUtils';
import { Textarea } from '@/components/ui/textarea';
import { useLanguage } from '@/i18n/LanguageContext';

function PayrollReportsPage() {
  const { t, isRTL } = useLanguage();
  const [activeTab, setActiveTab] = useState('timesheet');
  const [dateRange, setDateRange] = useState('monthly');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [selectedStaff, setSelectedStaff] = useState('all');
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [staffList, setStaffList] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const printRef = useRef();
  
  // Payment modal state
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedStaffForPayment, setSelectedStaffForPayment] = useState(null);
  const [paymentType, setPaymentType] = useState('full');
  const [customAmount, setCustomAmount] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [processingPayment, setProcessingPayment] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState({}); // { staff_id: { is_paid, amount, paid_at, currency } }

  // Initialize date range
  useEffect(() => {
    setDateRangePreset('monthly');
    fetchStaffList();
  }, []);

  const setDateRangePreset = (preset) => {
    const today = new Date();
    let start, end;

    switch (preset) {
      case 'daily':
        start = end = today;
        break;
      case 'weekly':
        start = new Date(today);
        start.setDate(today.getDate() - today.getDay());
        end = new Date(start);
        end.setDate(start.getDate() + 6);
        break;
      case 'monthly':
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        end = today;
        break;
      case 'custom':
        return;
      default:
        start = end = today;
    }

    setFromDate(start.toISOString().split('T')[0]);
    setToDate(end.toISOString().split('T')[0]);
    setDateRange(preset);
  };

  const fetchStaffList = async () => {
    try {
      const response = await axios.get(`${API}/admin/staff`, { withCredentials: true });
      setStaffList(response.data);
      // Extract unique departments
      const depts = [...new Set(response.data.map(s => s.department).filter(Boolean))];
      setDepartments(depts);
    } catch (error) {
      console.error('Failed to fetch staff:', error);
    }
  };

  const handleGenerateReport = async () => {
    setLoading(true);
    setReportData(null);

    try {
      const params = {
        start_date: fromDate,
        end_date: toDate
      };

      if (selectedStaff !== 'all') params.staff_id = selectedStaff;
      if (selectedDepartment !== 'all') params.department = selectedDepartment;

      const endpoints = {
        timesheet: '/admin/payroll-reports/timesheet',
        salary: '/admin/payroll-reports/salary-calculation',
        overtime: '/admin/payroll-reports/overtime',
        leave: '/admin/payroll-reports/leave-absence',
        audit: '/admin/payroll-reports/audit'
      };

      const response = await axios.get(`${API}${endpoints[activeTab]}`, {
        params,
        withCredentials: true
      });
      setReportData(response.data);
    } catch (error) {
      console.error('Failed to generate report:', error);
    } finally {
      setLoading(false);
    }
  };

  // Export to CSV
  const handleExportCSV = () => {
    if (!reportData?.records?.length) return;

    let csvContent = '';
    const records = reportData.records;

    // Create headers based on report type
    const headers = Object.keys(records[0]).filter(k => k !== 'daily_breakdown' && k !== 'absence_details' && k !== 'ot_daily');
    csvContent = headers.join(',') + '\n';

    // Add data rows
    records.forEach(record => {
      const row = headers.map(h => {
        const val = record[h];
        return typeof val === 'string' && val.includes(',') ? `"${val}"` : val ?? '';
      });
      csvContent += row.join(',') + '\n';
    });

    // Download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${activeTab}-report-${fromDate}-to-${toDate}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Export to PDF (print)
  const handleExportPDF = () => {
    const printWindow = window.open('', '_blank');
    const reportTitle = {
      timesheet: 'Timesheet Report',
      salary: 'Salary Calculation Report',
      overtime: 'Overtime Report',
      leave: 'Leave & Absence Report',
      audit: 'Payroll Audit Report'
    }[activeTab];

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>${reportTitle}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { color: #333; border-bottom: 2px solid #6366f1; padding-bottom: 10px; }
          .summary { background: #f3f4f6; padding: 15px; border-radius: 8px; margin: 20px 0; }
          .summary-item { display: inline-block; margin-right: 30px; }
          .summary-label { font-size: 12px; color: #666; }
          .summary-value { font-size: 18px; font-weight: bold; color: #333; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th { background: #6366f1; color: white; padding: 12px 8px; text-align: left; font-size: 12px; }
          td { padding: 10px 8px; border-bottom: 1px solid #e5e7eb; font-size: 11px; }
          tr:nth-child(even) { background: #f9fafb; }
          .period { color: #666; font-size: 14px; margin-bottom: 20px; }
          .positive { color: #16a34a; }
          .negative { color: #dc2626; }
          @media print { body { padding: 0; } }
        </style>
      </head>
      <body>
        <h1>${reportTitle}</h1>
        <p class="period">Period: ${fromDate} to ${toDate}</p>
        ${generatePDFContent()}
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const generatePDFContent = () => {
    if (!reportData) return '';

    let html = '<div class="summary">';
    Object.entries(reportData.summary || {}).forEach(([key, value]) => {
      const label = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      const formattedValue = typeof value === 'number' && key.includes('cost') || key.includes('pay') || key.includes('salary') || key.includes('deduction')
        ? `$${value.toLocaleString()}`
        : value.toLocaleString();
      html += `<div class="summary-item"><div class="summary-label">${label}</div><div class="summary-value">${formattedValue}</div></div>`;
    });
    html += '</div>';

    if (reportData.records?.length) {
      const excludeFields = ['daily_breakdown', 'absence_details', 'ot_daily', 'staff_id'];
      const headers = Object.keys(reportData.records[0]).filter(k => !excludeFields.includes(k));

      html += '<table><thead><tr>';
      headers.forEach(h => {
        html += `<th>${h.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</th>`;
      });
      html += '</tr></thead><tbody>';

      reportData.records.forEach(record => {
        html += '<tr>';
        headers.forEach(h => {
          let val = record[h];
          let className = '';
          if (typeof val === 'number') {
            if (h.includes('deduction') || h.includes('penalty') || h.includes('tax')) {
              className = 'negative';
              val = val > 0 ? `-$${val.toLocaleString()}` : '-';
            } else if (h.includes('bonus') || h.includes('overtime_pay')) {
              className = 'positive';
              val = val > 0 ? `+$${val.toLocaleString()}` : '-';
            } else if (h.includes('salary') || h.includes('pay') || h.includes('cost') || h.includes('rate')) {
              val = `$${val.toLocaleString()}`;
            }
          }
          html += `<td class="${className}">${val ?? '-'}</td>`;
        });
        html += '</tr>';
      });
      html += '</tbody></table>';
    }

    return html;
  };

  // Currency info helper
  const CURRENCY_SYMBOLS = {
    'USD': '$', 'EUR': '€', 'GBP': '£', 'JPY': '¥', 'CNY': '¥', 'CHF': 'CHF',
    'CAD': 'C$', 'AUD': 'A$', 'NZD': 'NZ$', 'INR': '₹', 'PKR': 'Rs', 'BDT': '৳',
    'LKR': 'Rs', 'NPR': 'Rs', 'SGD': 'S$', 'MYR': 'RM', 'THB': '฿', 'IDR': 'Rp',
    'PHP': '₱', 'VND': '₫', 'KRW': '₩', 'TWD': 'NT$', 'HKD': 'HK$', 'AED': 'د.إ',
    'SAR': '﷼', 'QAR': 'ر.ق', 'KWD': 'د.ك', 'BHD': '.د.ب', 'OMR': 'ر.ع.', 'JOD': 'د.ا',
    'ILS': '₪', 'IRR': '﷼', 'TRY': '₺', 'RUB': '₽', 'PLN': 'zł', 'CZK': 'Kč',
    'HUF': 'Ft', 'RON': 'lei', 'BGN': 'лв', 'HRK': 'kn', 'SEK': 'kr', 'NOK': 'kr',
    'DKK': 'kr', 'UAH': '₴', 'ZAR': 'R', 'NGN': '₦', 'EGP': 'E£', 'KES': 'KSh',
    'GHS': '₵', 'MAD': 'د.م.', 'BRL': 'R$', 'MXN': 'Mex$', 'ARS': 'AR$', 'CLP': 'CLP$',
    'COP': 'COL$', 'PEN': 'S/'
  };

  const formatCurrency = (value, currency = 'USD') => {
    if (!value && value !== 0) return '-';
    const symbol = CURRENCY_SYMBOLS[currency] || currency;
    return `${symbol} ${value.toLocaleString()}`;
  };

  const formatHours = (hours) => {
    if (!hours && hours !== 0) return '-';
    return `${hours.toFixed(1)}h`;
  };

  // Check payment status for all staff in report
  const checkPaymentStatuses = async () => {
    if (!reportData?.records?.length) return;
    
    const details = {};
    for (const record of reportData.records) {
      try {
        const response = await axios.get(`${API}/admin/salary-payments/check`, {
          params: {
            staff_id: record.staff_id,
            period_start: fromDate,
            period_end: toDate
          },
          withCredentials: true
        });
        details[record.staff_id] = {
          is_paid: response.data.is_paid,
          amount: response.data.payment?.amount || 0,
          paid_at: response.data.payment?.paid_at || null,
          currency: response.data.payment?.currency || record.currency
        };
      } catch (error) {
        details[record.staff_id] = { is_paid: false, amount: 0, paid_at: null, currency: record.currency };
      }
    }
    setPaymentDetails(details);
  };

  // Calculate payment summary
  const getPaymentSummary = () => {
    if (!reportData?.records?.length) return { total: 0, paid: 0, unpaid: 0, paidCount: 0, unpaidCount: 0 };
    
    let totalSalary = 0;
    let paidSalary = 0;
    let paidCount = 0;
    let unpaidCount = 0;
    
    reportData.records.forEach(record => {
      totalSalary += record.net_salary || 0;
      const payment = paymentDetails[record.staff_id];
      if (payment?.is_paid) {
        paidSalary += payment.amount || 0;
        paidCount++;
      } else {
        unpaidCount++;
      }
    });
    
    return {
      total: totalSalary,
      paid: paidSalary,
      unpaid: totalSalary - paidSalary,
      paidCount,
      unpaidCount
    };
  };

  // Format date for display
  const formatPaymentDate = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Check payment status when report data changes
  useEffect(() => {
    if (reportData && activeTab === 'salary') {
      checkPaymentStatuses();
    }
  }, [reportData, activeTab]);

  // Open payment modal
  const handleOpenPaymentModal = (staffRecord) => {
    setSelectedStaffForPayment(staffRecord);
    setPaymentType('full');
    setCustomAmount('');
    setPaymentNotes('');
    setPaymentModalOpen(true);
  };

  // Process payment
  const handleProcessPayment = async () => {
    if (!selectedStaffForPayment) return;
    
    const amount = paymentType === 'full' 
      ? selectedStaffForPayment.net_salary 
      : parseFloat(customAmount);
    
    if (!amount || amount <= 0) {
      alert('Please enter a valid amount');
      return;
    }
    
    setProcessingPayment(true);
    
    try {
      const response = await axios.post(`${API}/admin/salary-payments`, {
        staff_id: selectedStaffForPayment.staff_id,
        payment_type: paymentType,
        amount: amount,
        period_start: fromDate,
        period_end: toDate,
        notes: paymentNotes || null
      }, { withCredentials: true });
      
      // Update payment details with full info
      setPaymentDetails(prev => ({
        ...prev,
        [selectedStaffForPayment.staff_id]: {
          is_paid: true,
          amount: amount,
          paid_at: response.data.payment?.paid_at || new Date().toISOString(),
          currency: selectedStaffForPayment.currency
        }
      }));
      
      setPaymentModalOpen(false);
      setSelectedStaffForPayment(null);
    } catch (error) {
      console.error('Payment failed:', error);
      alert(error.response?.data?.detail || 'Failed to process payment');
    } finally {
      setProcessingPayment(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="payroll-reports-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Payroll Reports</h2>
          <p className="text-gray-600 mt-1">Generate detailed payroll and financial reports</p>
        </div>
      </div>

      {/* Filters Card */}
      <Card className="border-2 border-indigo-100 shadow-md" data-testid="report-filters">
        <CardHeader className="bg-gradient-to-r from-indigo-50 to-purple-50 border-b">
          <CardTitle className="flex items-center gap-2 text-indigo-800">
            <FileSpreadsheet className="w-5 h-5" />
            Report Filters
          </CardTitle>
          <CardDescription>Select date range, staff, and department to generate reports</CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
            {/* Date Range Preset */}
            <div className="space-y-2">
              <Label className="text-sm font-medium flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-500" />
                Date Range
              </Label>
              <Select value={dateRange} onValueChange={setDateRangePreset}>
                <SelectTrigger data-testid="date-range-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Today</SelectItem>
                  <SelectItem value="weekly">This Week</SelectItem>
                  <SelectItem value="monthly">This Month</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* From Date */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">From Date</Label>
              <Input
                type="date"
                value={fromDate}
                onChange={(e) => { setFromDate(e.target.value); setDateRange('custom'); }}
                data-testid="from-date-input"
              />
            </div>

            {/* To Date */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">To Date</Label>
              <Input
                type="date"
                value={toDate}
                onChange={(e) => { setToDate(e.target.value); setDateRange('custom'); }}
                data-testid="to-date-input"
              />
            </div>

            {/* Department Filter */}
            <div className="space-y-2">
              <Label className="text-sm font-medium flex items-center gap-2">
                <Briefcase className="w-4 h-4 text-gray-500" />
                Department
              </Label>
              <Select value={selectedDepartment} onValueChange={setSelectedDepartment}>
                <SelectTrigger data-testid="department-filter">
                  <SelectValue placeholder="All Departments" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Departments</SelectItem>
                  {departments.map((dept) => (
                    <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Staff Filter */}
            <div className="space-y-2">
              <Label className="text-sm font-medium flex items-center gap-2">
                <Users className="w-4 h-4 text-gray-500" />
                Staff
              </Label>
              <Select value={selectedStaff} onValueChange={setSelectedStaff}>
                <SelectTrigger data-testid="staff-filter">
                  <SelectValue placeholder="All Staff" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Staff</SelectItem>
                  {staffList.map((staff) => (
                    <SelectItem key={staff.staff_id} value={staff.staff_id}>{staff.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Generate Button */}
            <div className="space-y-2">
              <Label>&nbsp;</Label>
              <Button
                onClick={handleGenerateReport}
                className="w-full bg-indigo-600 hover:bg-indigo-700"
                disabled={loading}
                data-testid="generate-report-btn"
              >
                {loading ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <FileText className="w-4 h-4 mr-2" />
                    Generate
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Report Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setReportData(null); }} className="w-full">
        <TabsList className="grid grid-cols-5 w-full bg-gray-100 p-1 rounded-xl">
          <TabsTrigger value="timesheet" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="timesheet-tab">
            <Timer className="w-4 h-4 mr-2" />
            Timesheet
          </TabsTrigger>
          <TabsTrigger value="salary" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="salary-tab">
            <DollarSign className="w-4 h-4 mr-2" />
            Salary
          </TabsTrigger>
          <TabsTrigger value="overtime" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="overtime-tab">
            <TrendingUp className="w-4 h-4 mr-2" />
            Overtime
          </TabsTrigger>
          <TabsTrigger value="leave" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="leave-tab">
            <AlertTriangle className="w-4 h-4 mr-2" />
            Leave/Absence
          </TabsTrigger>
          <TabsTrigger value="audit" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="audit-tab">
            <ClipboardList className="w-4 h-4 mr-2" />
            Audit
          </TabsTrigger>
        </TabsList>

        {/* Timesheet Report */}
        <TabsContent value="timesheet" className="mt-6">
          {reportData && activeTab === 'timesheet' ? (
            <Card className="shadow-lg" data-testid="timesheet-report">
              <CardHeader className="border-b bg-gradient-to-r from-blue-50 to-cyan-50">
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-blue-800">
                      <Timer className="w-5 h-5" />
                      Timesheet Report
                    </CardTitle>
                    <CardDescription>{reportData.summary?.total_staff} staff • {fromDate} to {toDate}</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={handleExportCSV} data-testid="export-csv-btn">
                      <Download className="w-4 h-4 mr-2" /> CSV
                    </Button>
                    <Button variant="outline" onClick={handleExportPDF} data-testid="export-pdf-btn">
                      <Printer className="w-4 h-4 mr-2" /> PDF
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {/* Summary */}
                <div className="grid grid-cols-4 gap-4 p-4 bg-gray-50 border-b">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-blue-600">{formatHours(reportData.summary?.total_regular_hours)}</p>
                    <p className="text-sm text-gray-500">Regular Hours</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-purple-600">{formatHours(reportData.summary?.total_overtime_hours)}</p>
                    <p className="text-sm text-gray-500">Overtime Hours</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-orange-600">{formatHours(reportData.summary?.total_break_hours)}</p>
                    <p className="text-sm text-gray-500">Break Hours</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-gray-600">{reportData.summary?.total_staff}</p>
                    <p className="text-sm text-gray-500">{t("dashboard.total_staff")}</p>
                  </div>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead>Staff</TableHead>
                      <TableHead>{t("staff.department")}</TableHead>
                      <TableHead className="text-center">Shift Hours</TableHead>
                      <TableHead className="text-center">Regular Hours</TableHead>
                      <TableHead className="text-center">Overtime</TableHead>
                      <TableHead className="text-center">Break Time</TableHead>
                      <TableHead className="text-center">Days Worked</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reportData.records?.map((record, i) => (
                      <TableRow key={i} className="hover:bg-gray-50">
                        <TableCell className="font-medium">{record.staff_name}</TableCell>
                        <TableCell><Badge variant="outline">{record.department}</Badge></TableCell>
                        <TableCell className="text-center">{record.shift_hours}h</TableCell>
                        <TableCell className="text-center">
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded font-mono">{formatHours(record.regular_hours)}</span>
                        </TableCell>
                        <TableCell className="text-center">
                          {record.overtime_hours > 0 ? (
                            <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded font-mono">{formatHours(record.overtime_hours)}</span>
                          ) : '-'}
                        </TableCell>
                        <TableCell className="text-center">
                          <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded font-mono">{formatHours(record.break_hours)}</span>
                        </TableCell>
                        <TableCell className="text-center font-semibold">{record.days_worked}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-dashed border-2 border-gray-300">
              <CardContent className="py-16 text-center">
                <Timer className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900">Timesheet Report</h3>
                <p className="text-gray-500 mt-2">Regular hours, overtime, break time, and approved hours per staff</p>
                <p className="text-sm text-gray-400 mt-4">Select date range and click Generate</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Salary Calculation Report */}
        <TabsContent value="salary" className="mt-6">
          {reportData && activeTab === 'salary' ? (
            <Card className="shadow-lg" data-testid="salary-report">
              <CardHeader className="border-b bg-gradient-to-r from-indigo-50 to-indigo-50">
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-indigo-800">
                      <DollarSign className="w-5 h-5" />
                      Salary Calculation Report
                    </CardTitle>
                    <CardDescription>{reportData.summary?.total_staff} staff with salary setup</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={handleExportCSV}><Download className="w-4 h-4 mr-2" /> CSV</Button>
                    <Button variant="outline" onClick={handleExportPDF}><Printer className="w-4 h-4 mr-2" /> PDF</Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {/* Payment Summary Cards */}
                <div className="grid grid-cols-3 gap-4 p-4 border-b">
                  <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
                    <CardContent className="pt-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-blue-600 font-medium">Total Salaries</p>
                          <p className="text-2xl font-bold text-blue-800">{formatCurrency(getPaymentSummary().total)}</p>
                          <p className="text-xs text-blue-500">{reportData.summary?.total_staff || 0} staff</p>
                        </div>
                        <div className="p-3 bg-blue-100 rounded-full">
                          <DollarSign className="w-6 h-6 text-blue-600" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-indigo-50 to-indigo-50 border-indigo-200">
                    <CardContent className="pt-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-indigo-600 font-medium">Paid Salaries</p>
                          <p className="text-2xl font-bold text-indigo-800">{formatCurrency(getPaymentSummary().paid)}</p>
                          <p className="text-xs text-indigo-500">{getPaymentSummary().paidCount} staff paid</p>
                        </div>
                        <div className="p-3 bg-indigo-100 rounded-full">
                          <CheckCircle className="w-6 h-6 text-indigo-600" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
                    <CardContent className="pt-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-amber-600 font-medium">Unpaid Salaries</p>
                          <p className="text-2xl font-bold text-amber-800">{formatCurrency(getPaymentSummary().unpaid)}</p>
                          <p className="text-xs text-amber-500">{getPaymentSummary().unpaidCount} staff pending</p>
                        </div>
                        <div className="p-3 bg-amber-100 rounded-full">
                          <Clock className="w-6 h-6 text-amber-600" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
                
                {/* Calculation Summary */}
                <div className="grid grid-cols-6 gap-4 p-4 bg-gray-50 border-b">
                  <div className="text-center">
                    <p className="text-xl font-bold text-gray-600">{formatCurrency(reportData.summary?.total_base_pay)}</p>
                    <p className="text-xs text-gray-500">Base Pay</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xl font-bold text-indigo-600">+{formatCurrency(reportData.summary?.total_overtime_pay)}</p>
                    <p className="text-xs text-gray-500">Overtime</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xl font-bold text-blue-600">+{formatCurrency(reportData.summary?.total_bonuses)}</p>
                    <p className="text-xs text-gray-500">Bonuses</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xl font-bold text-red-600">-{formatCurrency(reportData.summary?.total_deductions)}</p>
                    <p className="text-xs text-gray-500">Deductions</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xl font-bold text-orange-600">-{formatCurrency(reportData.summary?.total_tax)}</p>
                    <p className="text-xs text-gray-500">Tax</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xl font-bold text-purple-600">{formatCurrency(reportData.summary?.total_net_pay)}</p>
                    <p className="text-xs text-gray-500">Net Pay</p>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead>Staff</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead className="text-right">Base Pay</TableHead>
                        <TableHead className="text-right">Overtime</TableHead>
                        <TableHead className="text-right">Bonuses</TableHead>
                        <TableHead className="text-right">Deductions</TableHead>
                        <TableHead className="text-right">Tax</TableHead>
                        <TableHead className="text-right">Net Salary</TableHead>
                        <TableHead className="text-center">{t("common.status")}</TableHead>
                        <TableHead className="text-right">Paid Amount</TableHead>
                        <TableHead className="text-center">Payment Date</TableHead>
                        <TableHead className="text-center">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {reportData.records?.map((record, i) => {
                        const payment = paymentDetails[record.staff_id];
                        return (
                          <TableRow key={i} className="hover:bg-gray-50">
                            <TableCell>
                              <div>
                                <p className="font-medium">{record.staff_name}</p>
                                <p className="text-xs text-gray-500">{record.department}</p>
                              </div>
                            </TableCell>
                            <TableCell><Badge variant="outline">{record.salary_type}</Badge></TableCell>
                            <TableCell className="text-right">{formatCurrency(record.base_pay, record.currency)}</TableCell>
                            <TableCell className="text-right text-indigo-600">{record.overtime_pay > 0 ? `+${formatCurrency(record.overtime_pay, record.currency)}` : '-'}</TableCell>
                            <TableCell className="text-right text-blue-600">{record.bonuses > 0 ? `+${formatCurrency(record.bonuses, record.currency)}` : '-'}</TableCell>
                            <TableCell className="text-right text-red-600">{record.deductions > 0 ? `-${formatCurrency(record.deductions, record.currency)}` : '-'}</TableCell>
                            <TableCell className="text-right text-orange-600">{record.tax_amount > 0 ? `-${formatCurrency(record.tax_amount, record.currency)}` : '-'}</TableCell>
                            <TableCell className="text-right font-bold text-purple-600">{formatCurrency(record.net_salary, record.currency)}</TableCell>
                            <TableCell className="text-center">
                              {payment?.is_paid ? (
                                <Badge className="bg-indigo-100 text-indigo-700 hover:bg-indigo-100">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Paid
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-amber-600 border-amber-300">
                                  Unpaid
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              {payment?.is_paid ? (
                                <span className="font-semibold text-indigo-600">
                                  {formatCurrency(payment.amount, payment.currency || record.currency)}
                                </span>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              {payment?.is_paid ? (
                                <span className="text-sm text-gray-600">
                                  {formatPaymentDate(payment.paid_at)}
                                </span>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              {payment?.is_paid ? (
                                <span className="text-sm text-gray-400">-</span>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-indigo-600 border-indigo-300 hover:bg-indigo-50"
                                  onClick={() => handleOpenPaymentModal(record)}
                                  data-testid={`make-payment-btn-${record.staff_id}`}
                                >
                                  <Banknote className="w-4 h-4 mr-1" />
                                  Make Payment
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-dashed border-2 border-gray-300">
              <CardContent className="py-16 text-center">
                <DollarSign className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900">Salary Calculation Report</h3>
                <p className="text-gray-500 mt-2">Hourly/monthly salary, deductions, bonuses, final payable amount</p>
                <p className="text-sm text-gray-400 mt-4">Select date range and click Generate</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Overtime Report */}
        <TabsContent value="overtime" className="mt-6">
          {reportData && activeTab === 'overtime' ? (
            <Card className="shadow-lg" data-testid="overtime-report">
              <CardHeader className="border-b bg-gradient-to-r from-purple-50 to-pink-50">
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-purple-800">
                      <TrendingUp className="w-5 h-5" />
                      Overtime Report
                    </CardTitle>
                    <CardDescription>{reportData.summary?.total_staff_with_ot} staff with overtime</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={handleExportCSV}><Download className="w-4 h-4 mr-2" /> CSV</Button>
                    <Button variant="outline" onClick={handleExportPDF}><Printer className="w-4 h-4 mr-2" /> PDF</Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {/* Summary */}
                <div className="grid grid-cols-3 gap-4 p-4 bg-gray-50 border-b">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-purple-600">{formatHours(reportData.summary?.total_ot_hours)}</p>
                    <p className="text-sm text-gray-500">Total OT Hours</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-indigo-600">{formatCurrency(reportData.summary?.total_ot_cost)}</p>
                    <p className="text-sm text-gray-500">Total OT Cost</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-gray-600">{reportData.summary?.total_staff_with_ot}</p>
                    <p className="text-sm text-gray-500">Staff with OT</p>
                  </div>
                </div>
                
                {/* By Department */}
                {reportData.by_department?.length > 0 && (
                  <div className="p-4 border-b">
                    <h4 className="font-semibold mb-3">By Department</h4>
                    <div className="flex flex-wrap gap-3">
                      {reportData.by_department.map((dept, i) => (
                        <div key={i} className="bg-purple-50 rounded-lg p-3 min-w-[150px]">
                          <p className="font-medium text-purple-800">{dept.department}</p>
                          <p className="text-sm text-gray-600">{formatHours(dept.ot_hours)} • {formatCurrency(dept.ot_cost)}</p>
                          <p className="text-xs text-gray-400">{dept.staff_count} staff</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead>Staff</TableHead>
                      <TableHead>{t("staff.department")}</TableHead>
                      <TableHead className="text-right">Hourly Rate</TableHead>
                      <TableHead className="text-right">OT Rate (x{reportData.records?.[0]?.ot_multiplier || 1.5})</TableHead>
                      <TableHead className="text-right">OT Hours</TableHead>
                      <TableHead className="text-right">OT Cost</TableHead>
                      <TableHead className="text-center">OT Days</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reportData.records?.map((record, i) => (
                      <TableRow key={i} className="hover:bg-gray-50">
                        <TableCell className="font-medium">{record.staff_name}</TableCell>
                        <TableCell><Badge variant="outline">{record.department}</Badge></TableCell>
                        <TableCell className="text-right">{formatCurrency(record.hourly_rate, record.currency)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(record.ot_rate, record.currency)}</TableCell>
                        <TableCell className="text-right">
                          <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded font-mono font-semibold">{formatHours(record.total_ot_hours)}</span>
                        </TableCell>
                        <TableCell className="text-right font-bold text-indigo-600">{formatCurrency(record.total_ot_cost, record.currency)}</TableCell>
                        <TableCell className="text-center">{record.ot_days_count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-dashed border-2 border-gray-300">
              <CardContent className="py-16 text-center">
                <TrendingUp className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900">Overtime Report</h3>
                <p className="text-gray-500 mt-2">OT hours, OT cost, breakdown by staff and department</p>
                <p className="text-sm text-gray-400 mt-4">Select date range and click Generate</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Leave & Absence Report */}
        <TabsContent value="leave" className="mt-6">
          {reportData && activeTab === 'leave' ? (
            <Card className="shadow-lg" data-testid="leave-report">
              <CardHeader className="border-b bg-gradient-to-r from-red-50 to-orange-50">
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-red-800">
                      <AlertTriangle className="w-5 h-5" />
                      Leave & Absence Report
                    </CardTitle>
                    <CardDescription>{reportData.summary?.total_staff} staff members</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={handleExportCSV}><Download className="w-4 h-4 mr-2" /> CSV</Button>
                    <Button variant="outline" onClick={handleExportPDF}><Printer className="w-4 h-4 mr-2" /> PDF</Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {/* Summary */}
                <div className="grid grid-cols-4 gap-4 p-4 bg-gray-50 border-b">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-gray-600">{reportData.summary?.total_staff}</p>
                    <p className="text-sm text-gray-500">{t("dashboard.total_staff")}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-red-600">{reportData.summary?.total_absent_days}</p>
                    <p className="text-sm text-gray-500">Absent Days</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-amber-600">{reportData.summary?.total_late_days}</p>
                    <p className="text-sm text-gray-500">Late Days</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-red-600">{formatCurrency(reportData.summary?.total_salary_impact)}</p>
                    <p className="text-sm text-gray-500">Salary Impact</p>
                  </div>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead>Staff</TableHead>
                      <TableHead>{t("staff.department")}</TableHead>
                      <TableHead className="text-center">Expected</TableHead>
                      <TableHead className="text-center">Present</TableHead>
                      <TableHead className="text-center">Absent</TableHead>
                      <TableHead className="text-center">Late</TableHead>
                      <TableHead className="text-center">Attendance %</TableHead>
                      <TableHead className="text-right">Deduction</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reportData.records?.map((record, i) => (
                      <TableRow key={i} className="hover:bg-gray-50">
                        <TableCell className="font-medium">{record.staff_name}</TableCell>
                        <TableCell><Badge variant="outline">{record.department}</Badge></TableCell>
                        <TableCell className="text-center">{record.expected_days}</TableCell>
                        <TableCell className="text-center">
                          <span className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded">{record.present_days}</span>
                        </TableCell>
                        <TableCell className="text-center">
                          {record.absent_days > 0 ? (
                            <span className="px-2 py-1 bg-red-100 text-red-700 rounded font-semibold">{record.absent_days}</span>
                          ) : '-'}
                        </TableCell>
                        <TableCell className="text-center">
                          {record.late_days > 0 ? (
                            <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded">{record.late_days}</span>
                          ) : '-'}
                        </TableCell>
                        <TableCell className="text-center">
                          <span className={`font-semibold ${record.attendance_rate >= 90 ? 'text-indigo-600' : record.attendance_rate >= 75 ? 'text-amber-600' : 'text-red-600'}`}>
                            {record.attendance_rate}%
                          </span>
                        </TableCell>
                        <TableCell className="text-right text-red-600 font-semibold">
                          {record.absence_deduction > 0 ? `-${formatCurrency(record.absence_deduction, record.currency)}` : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-dashed border-2 border-gray-300">
              <CardContent className="py-16 text-center">
                <AlertTriangle className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900">Leave & Absence Report</h3>
                <p className="text-gray-500 mt-2">Paid/unpaid leaves, absence tracking, salary impact</p>
                <p className="text-sm text-gray-400 mt-4">Select date range and click Generate</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Payroll Audit Report */}
        <TabsContent value="audit" className="mt-6">
          {reportData && activeTab === 'audit' ? (
            <Card className="shadow-lg" data-testid="audit-report">
              <CardHeader className="border-b bg-gradient-to-r from-gray-50 to-slate-100">
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-gray-800">
                      <ClipboardList className="w-5 h-5" />
                      Payroll Audit Report
                    </CardTitle>
                    <CardDescription>{reportData.summary?.total_modifications} total modifications</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={handleExportPDF}><Printer className="w-4 h-4 mr-2" /> PDF</Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {/* Summary */}
                <div className="grid grid-cols-4 gap-4 p-4 bg-gray-50 border-b">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-blue-600">{reportData.summary?.total_manual_checkins}</p>
                    <p className="text-sm text-gray-500">Manual Check-ins</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-blue-600">{reportData.summary?.total_manual_checkouts}</p>
                    <p className="text-sm text-gray-500">Manual Check-outs</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-amber-600">{reportData.summary?.total_status_overrides}</p>
                    <p className="text-sm text-gray-500">Status Overrides</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-purple-600">{reportData.summary?.total_break_modifications}</p>
                    <p className="text-sm text-gray-500">Break Modifications</p>
                  </div>
                </div>

                {reportData.summary?.total_modifications === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    <ClipboardList className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                    <p>No manual modifications found in this period</p>
                  </div>
                ) : (
                  <div className="space-y-6 p-4">
                    {reportData.manual_checkins?.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-blue-800 mb-2">Manual Check-ins</h4>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>{t("common.date")}</TableHead>
                              <TableHead>Staff</TableHead>
                              <TableHead>{t("staff.department")}</TableHead>
                              <TableHead>Time</TableHead>
                              <TableHead>Modified By</TableHead>
                              <TableHead>Reason</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {reportData.manual_checkins.map((item, i) => (
                              <TableRow key={i}>
                                <TableCell>{item.date}</TableCell>
                                <TableCell className="font-medium">{item.staff_name}</TableCell>
                                <TableCell>{item.department}</TableCell>
                                <TableCell>{item.time ? formatTimeFromISO(item.time) : '-'}</TableCell>
                                <TableCell><Badge>{item.modified_by}</Badge></TableCell>
                                <TableCell className="text-gray-600">{item.reason}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}

                    {reportData.status_overrides?.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-amber-800 mb-2">Status Overrides</h4>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>{t("common.date")}</TableHead>
                              <TableHead>Staff</TableHead>
                              <TableHead>Original Status</TableHead>
                              <TableHead>New Status</TableHead>
                              <TableHead>Modified By</TableHead>
                              <TableHead>Reason</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {reportData.status_overrides.map((item, i) => (
                              <TableRow key={i}>
                                <TableCell>{item.date}</TableCell>
                                <TableCell className="font-medium">{item.staff_name}</TableCell>
                                <TableCell><Badge variant="outline">{item.original_status}</Badge></TableCell>
                                <TableCell><Badge>{item.new_status}</Badge></TableCell>
                                <TableCell>{item.modified_by}</TableCell>
                                <TableCell className="text-gray-600">{item.reason}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card className="border-dashed border-2 border-gray-300">
              <CardContent className="py-16 text-center">
                <ClipboardList className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900">Payroll Audit Report</h3>
                <p className="text-gray-500 mt-2">Manual adjustments, admin overrides, approval history</p>
                <p className="text-sm text-gray-400 mt-4">Select date range and click Generate</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Report Type Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="pt-4">
            <Timer className="w-6 h-6 text-blue-500 mb-2" />
            <h4 className="font-semibold text-sm">Timesheet</h4>
            <p className="text-xs text-gray-500">Hours worked by staff</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-indigo-500">
          <CardContent className="pt-4">
            <DollarSign className="w-6 h-6 text-indigo-500 mb-2" />
            <h4 className="font-semibold text-sm">Salary</h4>
            <p className="text-xs text-gray-500">Pay calculations</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="pt-4">
            <TrendingUp className="w-6 h-6 text-purple-500 mb-2" />
            <h4 className="font-semibold text-sm">Overtime</h4>
            <p className="text-xs text-gray-500">OT hours & cost</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-red-500">
          <CardContent className="pt-4">
            <AlertTriangle className="w-6 h-6 text-red-500 mb-2" />
            <h4 className="font-semibold text-sm">Leave/Absence</h4>
            <p className="text-xs text-gray-500">Attendance impact</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-gray-500">
          <CardContent className="pt-4">
            <ClipboardList className="w-6 h-6 text-gray-500 mb-2" />
            <h4 className="font-semibold text-sm">Audit</h4>
            <p className="text-xs text-gray-500">Manual changes log</p>
          </CardContent>
        </Card>
      </div>

      {/* Payment Modal */}
      <Dialog open={paymentModalOpen} onOpenChange={setPaymentModalOpen}>
        <DialogContent className="sm:max-w-md" data-testid="payment-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-indigo-600" />
              Make Payment
            </DialogTitle>
            <DialogDescription>
              Process salary payment for {selectedStaffForPayment?.staff_name}
            </DialogDescription>
          </DialogHeader>
          
          {selectedStaffForPayment && (
            <div className="space-y-4 py-4">
              {/* Staff Info */}
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-medium">{selectedStaffForPayment.staff_name}</p>
                    <p className="text-sm text-gray-500">{selectedStaffForPayment.department}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-500">Net Salary</p>
                    <p className="font-bold text-lg text-purple-600">
                      {formatCurrency(selectedStaffForPayment.net_salary, selectedStaffForPayment.currency)}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Period Info */}
              <div className="text-sm text-gray-600 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Payment Period: {fromDate} to {toDate}
              </div>
              
              {/* Payment Type Selection */}
              <div className="space-y-2">
                <Label>Payment Type</Label>
                <Select value={paymentType} onValueChange={setPaymentType}>
                  <SelectTrigger data-testid="payment-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full">
                      <div className="flex items-center gap-2">
                        <Banknote className="w-4 h-4 text-indigo-600" />
                        Full Salary - {formatCurrency(selectedStaffForPayment.net_salary, selectedStaffForPayment.currency)}
                      </div>
                    </SelectItem>
                    <SelectItem value="custom">
                      <div className="flex items-center gap-2">
                        <DollarSign className="w-4 h-4 text-blue-600" />
                        Custom Payment
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Custom Amount Input */}
              {paymentType === 'custom' && (
                <div className="space-y-2">
                  <Label htmlFor="custom-amount">Custom Amount</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                      {CURRENCY_SYMBOLS[selectedStaffForPayment.currency] || selectedStaffForPayment.currency}
                    </span>
                    <Input
                      id="custom-amount"
                      type="number"
                      step="0.01"
                      min="0"
                      value={customAmount}
                      onChange={(e) => setCustomAmount(e.target.value)}
                      className="pl-10"
                      placeholder="Enter amount"
                      data-testid="custom-amount-input"
                    />
                  </div>
                </div>
              )}
              
              {/* Payment Notes */}
              <div className="space-y-2">
                <Label htmlFor="payment-notes">Notes (Optional)</Label>
                <Textarea
                  id="payment-notes"
                  value={paymentNotes}
                  onChange={(e) => setPaymentNotes(e.target.value)}
                  placeholder="Add any notes for this payment..."
                  rows={2}
                  data-testid="payment-notes-input"
                />
              </div>
              
              {/* Payment Summary */}
              <div className="p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                <div className="flex justify-between items-center">
                  <span className="text-indigo-700 font-medium">Amount to Pay:</span>
                  <span className="text-xl font-bold text-indigo-700">
                    {formatCurrency(
                      paymentType === 'full' ? selectedStaffForPayment.net_salary : (parseFloat(customAmount) || 0),
                      selectedStaffForPayment.currency
                    )}
                  </span>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setPaymentModalOpen(false)}
              disabled={processingPayment}
            >
              Cancel
            </Button>
            <Button
              onClick={handleProcessPayment}
              disabled={processingPayment || (paymentType === 'custom' && (!customAmount || parseFloat(customAmount) <= 0))}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="confirm-payment-btn"
            >
              {processingPayment ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Confirm Payment
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default PayrollReportsPage;
